import { useEffect, useRef, useState } from 'react';
import { ImageIcon } from 'lucide-react';
import type { Attachment } from '../types/attachment';
import { fetchAuthenticatedObjectUrl, revokeObjectUrl } from '../utils/authenticatedDownload';
import { ITEM_IMAGE_ACCEPT, validateItemImageFile } from '../utils/itemImageValidation';

export interface ItemImageFieldProps {
  existingImage: Attachment | null;
  pendingFile: File | null;
  removeExisting: boolean;
  onFileSelect: (file: File | null) => void;
  onRemoveExisting: () => void;
  disabled?: boolean;
  readOnly?: boolean;
  error?: string | null;
  required?: boolean;
}

const ItemImageField = ({
  existingImage,
  pendingFile,
  removeExisting,
  onFileSelect,
  onRemoveExisting,
  disabled = false,
  readOnly = false,
  error,
  required = false,
}: ItemImageFieldProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [pendingPreviewUrl, setPendingPreviewUrl] = useState<string | null>(null);
  const [existingPreviewUrl, setExistingPreviewUrl] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    if (!pendingFile) {
      setPendingPreviewUrl(null);
      return;
    }

    const url = URL.createObjectURL(pendingFile);
    setPendingPreviewUrl(url);
    return () => {
      revokeObjectUrl(url);
    };
  }, [pendingFile]);

  useEffect(() => {
    if (!existingImage || removeExisting || pendingFile) {
      setExistingPreviewUrl(null);
      setPreviewError(null);
      setLoadingPreview(false);
      return;
    }

    let cancelled = false;
    setLoadingPreview(true);
    setPreviewError(null);

    fetchAuthenticatedObjectUrl(existingImage.download_url)
      .then((url) => {
        if (!cancelled) {
          setExistingPreviewUrl(url);
        } else {
          revokeObjectUrl(url);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPreviewError('Could not load image preview.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingPreview(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [existingImage, removeExisting, pendingFile]);

  useEffect(() => {
    return () => {
      revokeObjectUrl(existingPreviewUrl);
    };
  }, [existingPreviewUrl]);

  const previewUrl = pendingPreviewUrl ?? existingPreviewUrl;
  const showPlaceholder = !previewUrl && !loadingPreview;
  const hasImage = Boolean(pendingFile || (existingImage && !removeExisting));
  const canEdit = !readOnly && !disabled;

  const handleFileChange = (file: File | null) => {
    if (!file) {
      onFileSelect(null);
      return;
    }

    const validation = validateItemImageFile(file);
    if (!validation.valid) {
      setValidationError(validation.message);
      onFileSelect(null);
      if (inputRef.current) {
        inputRef.current.value = '';
      }
      return;
    }

    setValidationError(null);
    onFileSelect(file);
  };

  const handleRemove = () => {
    onFileSelect(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    if (pendingFile) {
      return;
    }
    if (existingImage) {
      onRemoveExisting();
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row gap-4 items-start">
        <div className="w-32 h-32 shrink-0 rounded-lg border border-gray-200 bg-gray-50 overflow-hidden flex items-center justify-center">
          {loadingPreview ? (
            <span className="text-xs text-gray-500">Loading…</span>
          ) : previewUrl ? (
            <img
              src={previewUrl}
              alt={pendingFile?.name ?? existingImage?.filename ?? 'Item image'}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="flex flex-col items-center gap-1 text-gray-400 px-2 text-center">
              <ImageIcon className="h-8 w-8" />
              {showPlaceholder && <span className="text-xs">No image</span>}
            </div>
          )}
        </div>

        <div className="flex-1 space-y-2 min-w-0">
          {canEdit && (
            <>
              <input
                ref={inputRef}
                type="file"
                accept={ITEM_IMAGE_ACCEPT}
                className="hidden"
                disabled={disabled}
                onChange={(e) => handleFileChange(e.target.files?.[0] ?? null)}
              />
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => inputRef.current?.click()}
                  className="px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-60"
                >
                  {hasImage ? 'Replace image' : 'Choose image'}
                </button>
                {hasImage && (
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={handleRemove}
                    className="px-3 py-1.5 border border-gray-300 rounded-md text-sm text-red-700 bg-white hover:bg-red-50 disabled:opacity-60"
                  >
                    Remove
                  </button>
                )}
              </div>
            </>
          )}

          <p className="text-xs text-gray-500">
            {required ? 'Required' : 'Optional'} catalog image. JPEG, PNG, GIF, or TIFF. Max 20 MB. One image per item — uploading
            replaces any existing image.
          </p>

          {pendingFile && (
            <p className="text-xs text-gray-600 truncate" title={pendingFile.name}>
              Selected: {pendingFile.name}
            </p>
          )}

          {existingImage && !removeExisting && !pendingFile && (
            <p className="text-xs text-gray-600 truncate" title={existingImage.filename}>
              Current: {existingImage.filename}
            </p>
          )}

          {removeExisting && !pendingFile && (
            <p className="text-xs text-amber-700">Image will be removed when you save.</p>
          )}

          {(error || validationError || previewError) && (
            <p className="text-sm text-red-600">{error || validationError || previewError}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ItemImageField;
